import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DragSource, DropTarget } from 'react-dnd';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  ButtonDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import { puckCollectionTarget, puckCollectionSource } from './DNDPuckCollection';

import { showModal } from '../../../../actions';

import { arrayMove } from '../../../../helpers';
import { thumbnailPlaceholder } from '../../../../helpers/Collections';
import ItemTypes from '../../../../helpers/DNDItemTypes';

const CollectionPickerItemBase = ({
  collection,
  itemKey,
  canReorder,
  isCollapsed,
  onOpenReplaceModal,
  onToggleCollapse,
  onDelete,
  onFieldChange,
  intl,
  connectDragSource,
  connectDropTarget,
  isDragging,
}) => {
  const [draftTitle, setDraftTitle] = useState(collection.title || '');
  const [draftSubtitle, setDraftSubtitle] = useState(collection.subtitle || '');

  useEffect(() => {
    setDraftTitle(collection.title || '');
  }, [collection.title]);

  useEffect(() => {
    setDraftSubtitle(collection.subtitle || '');
  }, [collection.subtitle]);

  const commitTitle = () => {
    if (draftTitle !== (collection.title || '')) {
      onFieldChange('title', draftTitle);
    }
  };

  const commitSubtitle = () => {
    if (draftSubtitle !== (collection.subtitle || '')) {
      onFieldChange('subtitle', draftSubtitle);
    }
  };

  return connectDropTarget((
    <div
      className={classnames('puck-collection-picker-item', {
        'is-reorderable': canReorder,
        'is-static': !canReorder,
        dragging: isDragging,
      })}
    >
      <div className="d-flex align-items-center gap-2 puck-collection-picker-item-header">
        {canReorder
          ? connectDragSource(
            <i
              className="fas fa-grip-vertical puck-collection-picker-drag"
            />,
          )
          : null}
        <img
          src={collection.featured_file
            ? collection.featured_file.urls.thumbnailmasonry
            : thumbnailPlaceholder}
          alt={collection.name}
          className="puck-collection-picker-thumb"

        />
        <span
          id={`col-name-${itemKey}`}
          className="small flex-grow-1 text-truncate ml-1 puck-collection-picker-name"
        >
          {collection.name}
        </span>
        {itemKey && (
          <UncontrolledTooltip target={`col-name-${itemKey}`} placement="top">
            {collection.name}
          </UncontrolledTooltip>
        )}
        <Button
          size="sm"
          color="link"
          className="p-0 px-1"
          onClick={onOpenReplaceModal}
          title={intl.formatMessage({ defaultMessage: 'Replace collection' })}
        >
          <i className="fas fa-pencil-alt mr-0 puck-collection-picker-action" />
        </Button>
        <Button
          size="sm"
          color="link"
          className="p-0 px-1"
          onClick={onToggleCollapse}
          title={isCollapsed
            ? intl.formatMessage({ defaultMessage: 'Expand' })
            : intl.formatMessage({ defaultMessage: 'Collapse' })}
        >
          <i
            className={classnames(
              'mr-0 fas puck-collection-picker-action',
              isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up',
            )}
          />
        </Button>
        <Button
          size="sm"
          color="link"
          className="p-0 px-1 text-danger"
          onClick={onDelete}
          title={intl.formatMessage({ defaultMessage: 'Remove' })}
        >
          <i className="mr-0 fas fa-trash" />
        </Button>
      </div>

      {!isCollapsed && (
        <div className="puck-collection-picker-fields">
          <Input
            type="text"
            bsSize="sm"
            name="title"
            placeholder={intl.formatMessage({ defaultMessage: 'Title' })}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            className="mb-1"
          />
          <Input
            type="text"
            bsSize="sm"
            name="subtitle"
            placeholder={intl.formatMessage({ defaultMessage: 'Subtitle' })}
            value={draftSubtitle}
            onChange={(e) => setDraftSubtitle(e.target.value)}
            onBlur={commitSubtitle}
            className="mb-0"
          />
        </div>
      )}
    </div>
  ));
};

CollectionPickerItemBase.propTypes = {
  collection: PropTypes.object.isRequired,
  itemKey: PropTypes.string.isRequired,
  canReorder: PropTypes.bool.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  onOpenReplaceModal: PropTypes.func.isRequired,
  onToggleCollapse: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
};

const DraggableCollectionPickerItem =
DragSource(ItemTypes.PAGE_SECTION_ITEM, puckCollectionSource, (DNDconnect, monitor) => ({
  connectDragSource: DNDconnect.dragSource(),
  isDragging: monitor.isDragging(),
  setDragging: () => {},
}))(DropTarget(ItemTypes.PAGE_SECTION_ITEM, puckCollectionTarget, DNDconnect => ({
  connectDropTarget: DNDconnect.dropTarget(),
}))(CollectionPickerItemBase));

const CollectionPicker = ({
  value, onChange, label, onShowModal,
}) => {
  const intl = useIntl();

  const collections = Array.isArray(value) ? value : [];
  const labelWithCount = `${label} (${collections.length})`;

  useEffect(() => {
    if (collections.some((c) => !c.localKey)) {
      onChange(collections.map((c) => (c.localKey ? c : { ...c, localKey: uuidv4() })));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const withDefaultCollectionTexts = (collection) => {
    const defaultTitle = collection?.title || collection?.name || '';
    const defaultSubtitle = collection?.subtitle || collection?.description || '';

    return {
      ...collection,
      title: collection?.title || defaultTitle,
      subtitle: collection?.subtitle || defaultSubtitle,
    };
  };

  const prepareCollection = (collection) => ({
    ...withDefaultCollectionTexts(collection),
    localKey: uuidv4(),
  });

  const handleAddAtEnd = (collection) => {
    onChange([...collections, prepareCollection(collection)]);
  };

  const handleAddAtBeginning = (collection) => {
    onChange([prepareCollection(collection), ...collections]);
  };

  const handleDelete = (localKey) => {
    onChange(collections.filter((c) => c.localKey !== localKey));
  };

  const handleFieldChange = (localKey, field, val) => {
    onChange(collections.map((c) => (c.localKey === localKey ? { ...c, [field]: val } : c)));
  };

  const openModal = (onSelect) => {
    onShowModal('CollectionSelectModal', {
      className: 'modal-collection-select',
      backdrop: true,
      handleCollectionSelect: onSelect,
    });
  };

  const [collapsedKeys, setCollapsedKeys] = useState({});
  const [addDropdownOpen, setAddDropdownOpen] = useState(false);

  const isCollapsed = (localKey) => collapsedKeys[localKey] !== false;

  const toggleCollapse = (localKey) => {
    setCollapsedKeys((prev) => ({ ...prev, [localKey]: !isCollapsed(localKey) }));
  };

  const allCollapsed = collections.every((c) => isCollapsed(c.localKey));

  const toggleAll = () => {
    const next = {};
    collections.forEach((c) => { next[c.localKey] = !allCollapsed; });
    setCollapsedKeys(next);
  };

  const toggleAddDropdown = () => {
    setAddDropdownOpen((prev) => !prev);
  };

  const canReorder = collections.length > 1;

  const handleMoveCollection = (dragIndex, hoverIndex) => {
    if (!canReorder) return;
    if (dragIndex < 0 || hoverIndex < 0 || dragIndex === hoverIndex) return;
    onChange(arrayMove(collections, dragIndex, hoverIndex));
  };

  return (
    <div className="puck-collection-picker">
      <div className="puck-collection-picker-field-label">
        <div className="puck-collection-picker-title">
          {labelWithCount}
        </div>
        <div className="d-flex gap-2 mb-2">
          {collections.length > 0 ? (
            <ButtonDropdown
              isOpen={addDropdownOpen}
              toggle={toggleAddDropdown}
              className="flex-grow-1 mr-1 d-flex"
            >
              <Button
                size="sm"
                color="primary"
                className="flex-grow-1"
                onClick={() => openModal(handleAddAtBeginning)}
              >
                {intl.formatMessage({ defaultMessage: 'Add Collection' })}
              </Button>
              <DropdownToggle color="primary" size="sm" split className="flex-grow-0 puck-collection-picker-add-toggle">
                <i className="fas fa-chevron-down mr-0" />
              </DropdownToggle>
              <DropdownMenu right>
                <DropdownItem onClick={() => openModal(handleAddAtEnd)}>
                  {intl.formatMessage({ defaultMessage: 'Add Collection at the end' })}
                </DropdownItem>
              </DropdownMenu>
            </ButtonDropdown>
          ) : (
            <Button size="sm" color="primary" onClick={() => openModal(handleAddAtEnd)} className="flex-grow-1 mr-1">
              {intl.formatMessage({ defaultMessage: 'Add Collection' })}
            </Button>
          )}
          {collections.length > 0 && (
            <Button
              size="sm"
              color="secondary"
              className="flex-grow-0"
              onClick={toggleAll}
              title={allCollapsed
                ? intl.formatMessage({ defaultMessage: 'Expand all' })
                : intl.formatMessage({ defaultMessage: 'Collapse all' })}
            >
              <i className={classnames('mr-0 fal fa-lg', allCollapsed ? 'fa-chevron-down' : 'fa-chevron-up')} />
            </Button>
          )}
        </div>

        <div className="puck-collection-picker-list">
          {collections.map((c, idx) => {
            const itemKey = c.localKey || `${c.uuid || 'collection'}-${idx}`;

            return (
              <DraggableCollectionPickerItem
                key={itemKey}
                id={itemKey}
                itemKey={itemKey}
                index={idx}
                canDrag={canReorder}
                moveCollection={handleMoveCollection}
                collection={c}
                canReorder={canReorder}
                isCollapsed={isCollapsed(c.localKey || itemKey)}
                onOpenReplaceModal={() => openModal((replacement) => {
                  const preparedReplacement = withDefaultCollectionTexts(replacement);
                  onChange(
                    collections.map((col, colIdx) => {
                      const colKey = col.localKey || `${col.uuid || 'collection'}-${colIdx}`;
                      return colKey === itemKey
                        ? { ...preparedReplacement, localKey: c.localKey || itemKey }
                        : col;
                    }),
                  );
                })}
                onToggleCollapse={() => toggleCollapse(c.localKey || itemKey)}
                onDelete={() => {
                  if (!c.localKey) {
                    onChange(collections.filter((_, deleteIdx) => deleteIdx !== idx));
                    return;
                  }
                  handleDelete(c.localKey);
                }}
                onFieldChange={(field, val) => {
                  if (!c.localKey) {
                    onChange(
                      collections.map(
                        (col, colIdx) => (colIdx === idx ? { ...col, [field]: val } : col),
                      ),
                    );
                    return;
                  }
                  handleFieldChange(c.localKey, field, val);
                }}
                intl={intl}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

CollectionPicker.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  onShowModal: PropTypes.func.isRequired,
};

CollectionPicker.defaultProps = {
  value: [],
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
});

export const ConnectedCollectionPicker = connect(null, mapDispatchToProps)(CollectionPicker);
